<template>
    <div class="content-wrapper">
        <section class="content-header">
            <h1>
                {{ $t('batchContactsList.batchContactsLists') }}

                <div class="pull-right">
                    <router-link
                        :to="{ name: 'lists.batchContacts.create' }"
                        class="btn btn-sm btn-primary"
                    >
                        <span class="sr-only">{{ $t('addNew') }}</span>
                        <span class="hidden-xs">{{ $t('addNew') }}</span>
                        <span class="visible-xs"><i class="fa fa-plus"></i></span>
                    </router-link>
                </div>
            </h1>
        </section>

        <section class="content">
            <Alerts />

            <div class="row">
                <div class="col-xs-12">
                    <div class="box box-primary">
                        <div
                            v-if="!isLoaded"
                            class="box-body"
                        >
                            <img
                                class="table-row-preloader"
                                rel="preload"
                                src="/img/table-row-preloader.gif"
                            />
                        </div>

                        <template v-else-if="batchContactsLists.length">
                            <div class="box-header with-border">
                                <h3 class="box-title">
                                    {{ $t('batchContactsList.batchContactsListsTable') }}
                                </h3>

                                <div class="box-tools pull-right">
                                    <button
                                        class="btn btn-box-tool"
                                        :title="$t('collapse')"
                                        type="button"
                                        @click="isCollapse = !isCollapse"
                                    >
                                        <i
                                            class="fa"
                                            :class="{
                                                'fa-plus': !isCollapse,
                                                'fa-minus': isCollapse,
                                            }"
                                        />
                                    </button>
                                </div>
                            </div>

                            <vue-slide-up-down :active="isCollapse">
                                <div
                                    v-if="paginationOptions.total"
                                    class="box-body with-border clearfix"
                                >
                                    <BadgeTotal
                                        :value="paginationOptions.total"
                                        color="gray"
                                        :label="`${$t('total')}: `"
                                    />

                                    <div
                                        class="form-group"
                                        style="display: inline-flex; align-items: center; margin: 0 0 0 15px"
                                    >
                                        <label style="white-space: nowrap; margin: 0 10px 0 0"> With not active </label>

                                        <FormSwitcher
                                            :value="isShowNotActive"
                                            :disabled="isLoadingList"
                                            size="sm"
                                            @input="showNotActive"
                                        />
                                    </div>

                                    <div
                                        v-if="!isLoadingList && paginationOptions.last_page > 1"
                                        class="pagination-box"
                                    >
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchBatchContactsLists"
                                        />
                                    </div>
                                </div>

                                <div class="box-body table-responsive no-padding">
                                    <div
                                        v-if="isLoadingList"
                                        class="box-body"
                                    >
                                        <img
                                            class="table-row-preloader"
                                            rel="preload"
                                            src="/img/table-row-preloader.gif"
                                        />
                                    </div>

                                    <table
                                        v-else
                                        class="table table-striped table-hover"
                                    >
                                        <thead>
                                            <tr>
                                                <th>{{ $t('id') }}</th>
                                                <th style="min-width: 120px">
                                                    {{ $t('name') }}
                                                </th>
                                                <th>
                                                    {{ $t('active') }}
                                                </th>
                                                <th width="100px">
                                                    {{ $t('status') }}
                                                </th>
                                                <th>
                                                    {{ $t('batchContactsList.labels') }}
                                                </th>
                                                <th>
                                                    {{ $t('batchContactsList.totalContacts') }}
                                                </th>
                                                <th>
                                                    {{ $t('batchContactsList.readyContacts') }}
                                                </th>
                                                <th class="text-right">
                                                    {{ $t('createdAt') }}
                                                </th>
                                                <th class="text-right">
                                                    {{ $t('updatedAt') }}
                                                </th>
                                                <th></th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr
                                                v-for="batchContactsList in batchContactsLists"
                                                :key="batchContactsList.id"
                                            >
                                                <td>{{ batchContactsList.id }}</td>

                                                <td>
                                                    {{ batchContactsList.name }}
                                                </td>

                                                <td>
                                                    <ValueYesNo :value="Boolean(batchContactsList.active)" />
                                                </td>

                                                <td>
                                                    <span
                                                        class="label"
                                                        :class="{
                                                            'bg-primary': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_FRESH,
                                                            'bg-green': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_READY,
                                                            'bg-red': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_FAILED,
                                                            'bg-yellow': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_PROCESSING,
                                                            'bg-orange': batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_REFRESHING,
                                                        }"
                                                        :title="$t(batchContactsList.status.toLowerCase())"
                                                    >
                                                        {{ $t(batchContactsList.status.toLowerCase()) }}
                                                    </span>
                                                </td>

                                                <td>
                                                    <ListItems :rows="batchContactsList.labels" />
                                                </td>

                                                <td>
                                                    <BadgeTotal
                                                        :value="batchContactsList.all"
                                                        color="gray"
                                                    />
                                                </td>

                                                <td>
                                                    <BadgeTotal
                                                        :value="batchContactsList.ready"
                                                        color="green"
                                                    />
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(batchContactsList.createdAt) }}
                                                </td>

                                                <td class="text-right">
                                                    {{ formatDate(batchContactsList.updatedAt) }}
                                                </td>

                                                <td class="text-right text-nowrap">
                                                    <button
                                                        class="btn btn-sm btn-success"
                                                        data-toggle="modal"
                                                        data-target="#confirm-refresh-modal"
                                                        title="Refresh Batch Contacts"
                                                        :disabled="
                                                            batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_FRESH ||
                                                            batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_PROCESSING ||
                                                            batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_REFRESHING
                                                        "
                                                        @click="(isRefreshFailed = false), (refreshedBatchContactsList = batchContactsList)"
                                                    >
                                                        <i class="fa fa-refresh"></i>
                                                        <span class="sr-only">Refresh</span>
                                                    </button>

                                                    <button
                                                        class="btn btn-sm btn-danger"
                                                        data-toggle="modal"
                                                        data-target="#confirm-refresh-modal"
                                                        title="Refresh Batch Failed Contacts"
                                                        :disabled="
                                                            batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_FRESH ||
                                                            batchContactsList.status === BATCH_CONTACTS_LIST_STATUS_PROCESSING
                                                        "
                                                        @click="(isRefreshFailed = true), (refreshedBatchContactsList = batchContactsList)"
                                                    >
                                                        <i class="fa fa-refresh"></i>
                                                        <span class="sr-only">Refresh Failed</span>
                                                    </button>

                                                    <router-link
                                                        :to="{
                                                            name: 'lists.batchContacts.show',
                                                            params: {
                                                                id: batchContactsList.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-info"
                                                        :title="$t('show')"
                                                    >
                                                        <i class="fa fa-eye" />
                                                        <span class="sr-only">
                                                            {{ $t('show') }}
                                                        </span>
                                                    </router-link>

                                                    <router-link
                                                        :to="{
                                                            name: 'lists.batchContacts.edit',
                                                            params: {
                                                                id: batchContactsList.id,
                                                            },
                                                        }"
                                                        class="btn btn-sm btn-primary"
                                                        :title="$t('edit')"
                                                    >
                                                        <i class="fa fa-edit" />
                                                        <span class="sr-only">
                                                            {{ $t('edit') }}
                                                        </span>
                                                    </router-link>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div
                                    v-if="!isLoadingList && paginationOptions.last_page > 1"
                                    class="box-footer clearfix"
                                >
                                    <div class="pagination-box">
                                        <PaginationTable
                                            :options="paginationOptions"
                                            @change-page="fetchBatchContactsLists"
                                        />
                                    </div>
                                </div>
                            </vue-slide-up-down>
                        </template>

                        <div
                            v-else
                            class="box-body with-border clearfix"
                        >
                            <p>
                                {{ $t('messages.batchContactsList.noBatchContactsLists') }}
                                <router-link :to="{ name: 'lists.batchContacts.create' }">
                                    {{ $t('addNew') }}
                                </router-link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <ModalDialog
            id="confirm-refresh-modal"
            ref="modal"
            :class="{
                'modal-success': !isRefreshFailed,
                'modal-danger': isRefreshFailed,
            }"
            size="sm"
            :isLoading="isLoading"
            @submit="refreshBatchContactsList"
        >
            <template #header>{{ isRefreshFailed ? 'Refresh Failed Contacts?' : 'Refresh Contacts?' }}</template>

            <template #footer="{ close, submit }">
                <button
                    class="btn btn-sm btn-default"
                    type="button"
                    data-dismiss="modal"
                    :disabled="isLoading"
                    @click="close"
                >
                    {{ $t('cancel') }}
                </button>

                <button
                    class="btn btn-sm"
                    :class="{
                        'btn-success': !isRefreshFailed,
                        'btn-danger': isRefreshFailed,
                    }"
                    :disabled="isLoading"
                    @click="submit"
                >
                    <i
                        v-if="isLoading"
                        class="fa fa-refresh fa-spin"
                    />
                    <span>Refresh</span>
                </button>
            </template>
        </ModalDialog>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import PaginationTable from '@/components/subcomponents/PaginationTable';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import ValueYesNo from '@/components/subcomponents/ValueYesNo';
import FormSwitcher from '@/components/form/FormSwitcher';
import ModalDialog from '@/components/modals/ModalDialog';
import formatsMixin from '@/mixins/formatsMixin';
import batchContactsListMixin from '@/mixins/batchContactsListMixin';
import ListItems from "../../../components/subcomponents/ListItems.vue";

export default {
    name: 'BatchContactsList',
    components: {
        ListItems,
        PaginationTable,
        BadgeTotal,
        ValueYesNo,
        FormSwitcher,
        ModalDialog,
    },
    mixins: [formatsMixin, batchContactsListMixin],
    data() {
        return {
            isCollapse: true,
            isLoading: false,
            isLoadingList: false,
            isLoaded: false,
            isShowNotActive: false,
            timeout: 0,
            batchContactsLists: [],
            paginationOptions: {
                disabled: true,
                total: 0,
            },
            isRefreshFailed: false,
            refreshedBatchContactsList: {},
        };
    },
    created() {
        this.fetchBatchContactsLists(this.$route.query.page);
    },
    methods: {
        async fetchBatchContactsLists(page = 1) {
            try {
                this.$router
                    .replace({
                        query: {
                            ...this.$route.query,
                            page: page > 1 ? page : undefined,
                        },
                    })
                    .catch(() => {});

                this.paginationOptions.disabled = true;

                const {
                    data: {
                        batchContactsLists: { data: batchContactsLists = [], ...paginationOptions },
                    },
                } = await axios.get(`/lists/batch-contacts/data?page=${page}`, {
                    params: {
                        active: Number(!this.isShowNotActive),
                    },
                });

                this.batchContactsLists = batchContactsLists;
                this.paginationOptions = paginationOptions;

                this.checkProcessing();
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoaded = true;
                this.paginationOptions.disabled = false;
            }
        },

        async refreshBatchContactsList() {
            this.refreshedBatchContactsList.status = this.BATCH_CONTACTS_LIST_STATUS_REFRESHING;
            this.$refs.modal.close();

            try {
                if (this.isRefreshFailed) {
                    await axios.put(`/lists/batch-contacts/refresh-failed/${this.refreshedBatchContactsList.id}`);
                } else {
                    await axios.put(`/lists/batch-contacts/refresh/${this.refreshedBatchContactsList.id}`);
                }
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            }

            this.checkProcessing();
        },

        checkProcessing() {
            const isProcessing = !!this.batchContactsLists.find((batchContactsList) => {
                return (
                    batchContactsList.status === this.BATCH_CONTACTS_LIST_STATUS_FRESH ||
                    batchContactsList.status === this.BATCH_CONTACTS_LIST_STATUS_PROCESSING
                );
            });

            clearTimeout(this.timeout);

            if (isProcessing) {
                this.timeout = setTimeout(() => {
                    this.fetchBatchContactsLists();
                }, 10000);
            }
        },
        async showNotActive() {
            this.isShowNotActive = !this.isShowNotActive;
            this.isLoadingList = true;
            await this.fetchBatchContactsLists();
            this.isLoadingList = false;
        },
    },
};
</script>
